import React from 'react';

import EventTracking from 'lib/EventTracking';

import ErrorReporter from 'app/components/ErrorReporter';
import FormElement from 'components/FormElement';
import GetCancellationReasonsContainer from 'coaching/containers/GetCancellationReasonsContainer';
import Select from 'components/Select';

export type Props = {
  changeState: (reason: string) => void;
  eventPrefix?: string;
  sessionType: 'recurringSchedule' | 'instance';
  userType: 'participant' | 'coordinator';
  // the only reason this is passed in is so we can clear the cancellation reason when
  // a user clicks on a different instance/row in the sessions by subject container
  instanceUuid?: string;
};

export const CancellationReasonsSelect = ({
  changeState,
  eventPrefix,
  sessionType,
  userType,
  instanceUuid,
}: Props) => {
  const [selectedReason, setSelectedReason] = React.useState('');
  const isCoordinator = userType === 'coordinator';
  const trackKey = (key: string) => {
    eventPrefix && EventTracking.trackKey(`${eventPrefix}.${key}`);
  };

  React.useEffect(() => {
    !isCoordinator && trackKey('view');
  });

  React.useEffect(() => {
    setSelectedReason('');
  }, [instanceUuid]);

  return (
    <GetCancellationReasonsContainer sessionType={sessionType}>
      {({ cancellationReasons, error }) => {
        if (error || !cancellationReasons) {
          return <ErrorReporter error={error ?? 'Could not load cancellation reasons'} />;
        }
        return (
          <FormElement
            data-test="cancellation-reason-dropdown"
            label={isCoordinator ? 'Select a reason' : 'Reason for cancelling'}
            element={Select}
            elementProps={{
              options: cancellationReasons.map(reason => ({
                label: reason.uiLabel,
                value: reason.slug,
              })),
              value: selectedReason,
              name: 'reason',
            }}
            onValidChange={value => {
              setSelectedReason(value);
              changeState(value);
              !isCoordinator && trackKey('select_cancellation_reason.click');
            }}
          />
        );
      }}
    </GetCancellationReasonsContainer>
  );
};
